import {useEffect, useRef, useState} from 'react';
import {MoneyLabel} from './MoneyLabel.tsx';
import type {Money} from '../../lib/Money.ts';
import type {CurrencyEnum} from '../../generated/enums.ts';

export interface MonetaryOrigin {
    original_fx_currency: CurrencyEnum;
    original_fx_total_amount_payable: Money | null;
    original_fx_vat_amount_overall?: Money | null;
    fx_rate: string | null;
    fx_rate_market_date: string | null;
    fx_rate_source: string;
    fx_is_fallback: boolean;
}

const SOURCE_LABELS: Record<string, string> = {
    fastforex: 'dagkoers',
    ecb: 'ECB-dagkoers',
    manual: 'handmatig omgerekend',
    no_source_needed: 'geen omrekening nodig',
};

export function wasConvertedByHand(origin: MonetaryOrigin): boolean {
    return origin.fx_rate_source === 'manual';
}

export function formatExchangeRate(rate: string): string {
    return new Intl.NumberFormat('nl-NL', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    }).format(Number(rate));
}

export function formatMarketDate(marketDate: string): string {
    const parsed = new Date(marketDate);
    if (Number.isNaN(parsed.getTime())) {
        return marketDate;
    }
    return new Intl.DateTimeFormat('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(parsed);
}

function sourceLabel(origin: MonetaryOrigin): string {
    const label = SOURCE_LABELS[origin.fx_rate_source] ?? origin.fx_rate_source;
    return origin.fx_is_fallback ? `${label} (terugvalkoers)` : label;
}

interface Props {
    amount: Money;
    origin: MonetaryOrigin | null;
    variant?: 'inline' | 'disclosure';
    colorAid?: boolean;
}

export function ForeignAmountLabel({amount, origin, variant = 'disclosure', colorAid = false}: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (isOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }, [isOpen]);

    if (origin === null) {
        return (
            <MoneyLabel
                value={amount.signed_amount / 100}
                currency={amount.currency}
                showCurrency={true}
                colorAid={colorAid}
            />
        );
    }

    const billed = origin.original_fx_total_amount_payable;
    const rate = origin.fx_rate === null ? null : formatExchangeRate(origin.fx_rate);

    if (variant === 'inline') {
        return (
            <span className="whitespace-nowrap">
                <MoneyLabel
                    value={amount.signed_amount / 100}
                    currency={amount.currency}
                    showCurrency={true}
                    colorAid={colorAid}
                />
                {billed !== null && (
                    <span className="ml-1.5 text-gray-500">
                        (
                        <MoneyLabel
                            value={billed.signed_amount / 100}
                            currency={billed.currency}
                            showCurrency={true}
                        />
                        {rate !== null && `${' @ '}${rate}`})
                    </span>
                )}
            </span>
        );
    }

    return (
        <span ref={containerRef} className="relative inline-flex items-center gap-1">
            <MoneyLabel
                value={amount.signed_amount / 100}
                currency={amount.currency}
                showCurrency={true}
                colorAid={colorAid}
            />
            <button
                type="button"
                aria-label="Toon omrekening"
                aria-expanded={isOpen}
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <i className="ri-exchange-line text-sm leading-none" aria-hidden="true"/>
            </button>
            {isOpen && (
                <span
                    role="tooltip"
                    className="absolute left-0 top-full z-20 mt-1 w-60 rounded-lg bg-white p-3 text-left text-xs leading-5 text-gray-600 shadow-lg ring-1 ring-black ring-opacity-5"
                >
                    <span className="block font-medium text-gray-900">
                        {wasConvertedByHand(origin) ? 'Handmatig omgerekend' : 'Omgerekend naar euro'}
                    </span>
                    {billed !== null && (
                        <span className="mt-1 block">
                            Oorspronkelijk bedrag:{' '}
                            <MoneyLabel
                                value={billed.signed_amount / 100}
                                currency={billed.currency}
                                showCurrency={true}
                            />
                        </span>
                    )}
                    {rate !== null && (
                        <span className="block">
                            Koers: {rate} {origin.original_fx_currency}/EUR
                        </span>
                    )}
                    {origin.fx_rate_market_date !== null && (
                        <span className="block">
                            Koersdatum: {formatMarketDate(origin.fx_rate_market_date)}
                        </span>
                    )}
                    {!wasConvertedByHand(origin) && (
                        <span className="mt-1 block text-gray-400">Bron: {sourceLabel(origin)}</span>
                    )}
                </span>
            )}
        </span>
    );
}
